import React, { useEffect, useState } from 'react';
import { Loader2, Sparkles, Coins, MessageSquare, ExternalLink, TrendingUp } from 'lucide-react';
import api, { errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const Stat = ({ label, value, sub, icon: Icon }) => (
  <div className="rounded-2xl border border-rose/40 bg-white/70 p-5">
    <div className="flex items-center gap-2 text-mahogany/70 text-xs uppercase tracking-widest">{Icon && <Icon className="w-4 h-4" />} {label}</div>
    <div className="mt-2 font-serif-display text-3xl text-burgundy">{value}</div>
    {sub && <div className="text-sm text-mahogany/60 mt-1">{sub}</div>}
  </div>
);

const AdminAIUsage = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/ai-usage/summary').then((r) => setData(r.data)).catch((e) => toast({ title: 'Failed to load', description: errText(e) })).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div>;
  if (!data) return <p className="text-mahogany/60">No usage data yet.</p>;

  const m = data.this_month, a = data.all_time;
  const maxDaily = Math.max(1, ...(data.daily || []).map((d) => d.credits));

  return (
    <div className="space-y-8" data-testid="admin-ai-usage-page">
      <div>
        <h1 className="font-serif-display text-3xl text-burgundy flex items-center gap-2"><Sparkles className="w-6 h-6" /> AI Usage & Estimated Cost</h1>
        <p className="text-mahogany/70 text-sm">Estimated usage of your Emergent Universal Key by AI features on this site. 1 credit = $0.20 · no markup.</p>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-mahogany/60 mb-2">This month</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Stat label="Estimated cost" value={`${m.credits} cr`} sub={`≈ $${m.usd}`} icon={Coins} />
          <Stat label="AI actions" value={m.events} sub={`${(m.input_tokens + m.output_tokens).toLocaleString()} tokens`} icon={MessageSquare} />
          <Stat label="All-time cost" value={`${a.credits} cr`} sub={`≈ $${a.usd} · ${a.events} actions`} icon={TrendingUp} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-rose/40 bg-white/70 p-5">
          <h2 className="font-serif-display text-xl text-burgundy mb-3">By feature (all-time)</h2>
          {data.by_feature.length === 0 ? <p className="text-sm text-mahogany/60">No data yet.</p> : (
            <div className="space-y-2">
              {data.by_feature.map((f) => (
                <div key={f.feature} className="flex items-center justify-between text-sm">
                  <span className="text-burgundy">{f.feature}</span>
                  <span className="text-mahogany/70">{f.events} · {f.credits} cr (${f.usd})</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-rose/40 bg-white/70 p-5">
          <h2 className="font-serif-display text-xl text-burgundy mb-3">Most-asked topics</h2>
          {data.top_topics.length === 0 ? <p className="text-sm text-mahogany/60">No questions yet.</p> : (
            <div className="space-y-2">
              {data.top_topics.map((t) => (
                <div key={t.topic}>
                  <div className="flex justify-between text-sm text-burgundy"><span>{t.topic}</span><span className="text-mahogany/60">{t.count}</span></div>
                  <div className="h-2 rounded-full bg-rose-soft/50 mt-1"><div className="h-2 rounded-full bg-mahogany" style={{ width: `${(t.count / data.top_topics[0].count) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-rose/40 bg-white/70 p-5">
        <h2 className="font-serif-display text-xl text-burgundy mb-3">Last 30 days (est. credits/day)</h2>
        {data.daily.length === 0 ? <p className="text-sm text-mahogany/60">No usage recorded yet.</p> : (
          <div className="flex items-end gap-1 h-32">
            {data.daily.map((d) => (
              <div key={d.date} className="flex-1 group relative" title={`${d.date}: ${d.credits} cr (${d.events} actions)`}>
                <div className="bg-mahogany/80 hover:bg-mahogany rounded-t transition-colors" style={{ height: `${Math.max(3, (d.credits / maxDaily) * 120)}px` }} />
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-mahogany/55">
        These are close estimates tracked in-app (same rates as Emergent, no markup). Your official invoiced balance lives in
        {' '}<span className="font-medium">Account Settings → Credit Usage</span>. Tracking began when this feature was enabled — past activity isn't counted.
      </p>
    </div>
  );
};

export default AdminAIUsage;
